import { clsx } from 'clsx';
import { useLanguageOptional } from '@/contexts/LanguageContext';
import { AVAILABLE_LANGUAGES } from '@/i18n/languages';
import { t } from '@/i18n/t';

interface LanguageToggleProps {
  className?: string;
}

function LanguageSwitchIcon() {
  return (
    <svg viewBox="0 0 36 36" className="w-5 h-5 shrink-0" aria-hidden="true">
      <path fill="currentColor" d="M30 3H14v5h2V5h14c.6 0 1 .4 1 1v11c0 .6-.4 1-1 1H17v7h-5.3L8 27.9V25H5c-.6 0-1-.4-1-1V13c0-.6.4-1 1-1h13v-2H5c-1.7 0-3 1.3-3 3v11c0 1.7 1.3 3 3 3h1v5.1l6.3-5.1H19v-7h11c1.7 0 3-1.3 3-3V6c0-1.7-1.3-3-3-3" />
      <path fill="currentColor" d="M6.2 22.9h2.4l.6-1.6h3.1l.6 1.6h2.4L11.9 14H9.5zm4.5-6.4l1 3.1h-2z" />
      <path fill="currentColor" d="M20 17c1.1 0 2.6-.3 4-1c1.4.7 3 1 4 1v-2s-1 0-2.1-.4c1.2-1.2 2.1-3 2.1-5.6V8h-3V6h-2v2h-3v2h5.9c-.2 1.8-1 2.9-1.9 3.6c-.6-.5-1.2-1.2-1.6-2.1h-2.1c.4 1.3 1 2.3 1.8 3.1c-1 .4-1.9.4-2.1.4z" />
    </svg>
  );
}

/**
 * Round 66, side note — a header-level quick-access language switch,
 * visible on every learner screen the same way ThemeToggle's dark-mode
 * button is (both live in TopBar). LanguagePicker (profile/settings) is
 * still the deliberate, labeled way to change language; this is the
 * discoverable shortcut, not a replacement.
 *
 * TopBar is shared with AdminLayout.tsx, so this must self-hide on admin
 * screens — done structurally via useLanguageOptional() (returns null
 * outside a LanguageProvider) rather than a prop a future edit could
 * forget to pass. LanguageProvider only ever mounts inside
 * LearnerLayout.tsx, never above /admin, so the context is genuinely
 * absent there, not just unused.
 *
 * A native <select> layered invisibly over the visible icon: full
 * keyboard/screen-reader semantics for free, no custom popover/focus-trap
 * to build or maintain.
 */
export default function LanguageToggle({ className }: LanguageToggleProps) {
  const ctx = useLanguageOptional();
  if (!ctx) return null;
  const { language, setLanguage } = ctx;

  return (
    <div
      className={clsx(
        'relative inline-flex items-center justify-center rounded-lg min-h-touch min-w-touch',
        'text-muted hover:bg-paper-2 hover:text-ink transition-colors',
        className,
      )}
    >
      <LanguageSwitchIcon />
      <select
        aria-label={t('profile:languagePicker.language')}
        value={language}
        onChange={(e) => { void setLanguage(e.target.value); }}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
      >
        {AVAILABLE_LANGUAGES.map((l) => (
          <option key={l.code} value={l.code}>{l.nativeName}</option>
        ))}
      </select>
    </div>
  );
}
